import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { IndicativeBadge } from "@/components/IndicativeBadge";
import Footer from "@/components/Footer";
import SEOHead, { PAGE_SEO } from "@/components/SEOHead";
import TopOfTheMarts from "@/components/TopOfTheMarts";
import {
  TrendUp, TrendDown, Minus, Clock, MapPin, Newspaper,
  CaretRight, Globe, ChartLineUp, CalendarBlank, CircleNotch,
  Flag, Cow, Trophy
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRIES = [
  { id: "ireland", name: "Ireland", flag: "🇮🇪", link: "/irish-marts", color: "bg-[#0F766E]", description: "90+ livestock marts" },
  { id: "uk", name: "United Kingdom", flag: "🇬🇧", link: "/uk-markets", color: "bg-[#1E40AF]", description: "UK livestock markets" },
  { id: "usa", name: "United States", flag: "🇺🇸", link: "/usa-markets", color: "bg-[#B91C1C]", description: "US cattle auctions" },
];

export default function MartsPage() {
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [martNews, setMartNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch weekly price report
      const reportRes = await axios.get(`${API}/api/marts/weekly-report`);
      setWeeklyReport(reportRes.data);
      
      // Fetch mart-related news
      const newsRes = await axios.get(`${API}/api/articles`, {
        params: { category: "markets", limit: 6 }
      });
      setMartNews(newsRes.data || []);
    } catch (error) {
      console.error("Error fetching mart data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IE", { 
      weekday: "long",
      day: "numeric", 
      month: "long", 
      year: "numeric" 
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IE", { 
      day: "numeric", 
      month: "short"
    });
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <TrendUp size={16} className="text-green-600" weight="bold" />;
    if (change < 0) return <TrendDown size={16} className="text-red-600" weight="bold" />;
    return <Minus size={16} className="text-[#6B6661]" />;
  };

  const getChangeColor = (change) => {
    if (change > 0) return "text-green-600";
    if (change < 0) return "text-red-600";
    return "text-[#6B6661]";
  };

  return (
    <div className="min-h-screen bg-[#022C22]">
      <SEOHead {...PAGE_SEO.marts} url="/marts" />
      <Header />
      
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">
              Global Markets
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">
            Livestock Marts & Markets
          </h1>
          <div className="mb-2"><IndicativeBadge /></div>
          <p className="text-[#6EE7B7] font-sans">
            Indicative prices, weekly analysis, and news from livestock marts
          </p>
        </div>

        {/* Country Links */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          {COUNTRIES.map((country) => (
            <Link
              key={country.id}
              to={country.link}
              className={`${country.color} hover:opacity-90 transition-opacity p-4 text-white group`}
              data-testid={`country-${country.id}`}
            >
              <div className="text-3xl mb-2">{country.flag}</div>
              <div className="font-serif text-lg">{country.name}</div>
              <div className="text-xs text-white/70 mb-2">{country.description}</div>
              <div className="flex items-center gap-1 text-sm text-white/90 group-hover:text-white">
                View Markets <CaretRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Weekly Price Report Article */}
            {loading ? (
              <div className="bg-[#064E3B] border border-[#065F46] p-8 flex items-center justify-center">
                <CircleNotch size={32} className="animate-spin text-[#6EE7B7]" />
              </div>
            ) : weeklyReport ? (
              <article className="bg-[#064E3B] border border-[#065F46]">
                <div className="p-6 border-b border-[#065F46]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 bg-[#FBBF24] text-[#0A0A0A] text-xs font-mono uppercase">
                      Weekly Analysis
                    </span>
                    <span className="flex items-center gap-1 text-xs text-[#6EE7B7]">
                      <CalendarBlank size={12} />
                      {formatDate(weeklyReport.generated_at)}
                    </span>
                  </div>
                  <h2 className="font-serif text-2xl text-white mb-3">
                    {weeklyReport.title}
                  </h2>
                  <p className="text-[#A7F3D0] leading-relaxed">
                    {weeklyReport.summary}
                  </p>
                </div>

                {/* Price Summary Table */}
                <div className="p-6">
                  <h3 className="text-sm font-mono uppercase text-[#6EE7B7] mb-4 flex items-center gap-2">
                    <ChartLineUp size={16} />
                    7-Day Price Movement
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs text-[#6EE7B7] border-b border-[#065F46]">
                          <th className="pb-2">Category</th>
                          <th className="pb-2 text-right">Start</th>
                          <th className="pb-2 text-right">End</th>
                          <th className="pb-2 text-right">Change</th>
                          <th className="pb-2 text-right">High</th>
                          <th className="pb-2 text-right">Low</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {weeklyReport.categories?.map((cat, i) => (
                          <tr key={cat.name} className={`${i % 2 === 0 ? "bg-[#065F46]/30" : ""}`}>
                            <td className="py-2 text-white font-medium">{cat.name}</td>
                            <td className="py-2 text-right text-[#A7F3D0] font-mono">€{cat.start_price?.toFixed(2)}</td>
                            <td className="py-2 text-right text-white font-mono">€{cat.end_price?.toFixed(2)}</td>
                            <td className={`py-2 text-right font-mono flex items-center justify-end gap-1 ${getChangeColor(cat.change_percent)}`}>
                              {getChangeIcon(cat.change_percent)}
                              {cat.change_percent > 0 ? "+" : ""}{cat.change_percent?.toFixed(2)}%
                            </td>
                            <td className="py-2 text-right text-green-400 font-mono">€{cat.high?.toFixed(2)}</td>
                            <td className="py-2 text-right text-red-400 font-mono">€{cat.low?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Top Performers */}
                {weeklyReport.top_performers && (
                  <div className="p-6 border-t border-[#065F46]">
                    <h3 className="text-sm font-mono uppercase text-[#6EE7B7] mb-4 flex items-center gap-2">
                      <Trophy size={16} />
                      Top Performing Marts This Week
                    </h3>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {weeklyReport.top_performers.map((mart, i) => (
                        <div key={mart.name} className="bg-[#065F46] p-3 flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center font-bold text-sm ${
                            i === 0 ? "bg-[#FBBF24] text-[#0A0A0A]" :
                            i === 1 ? "bg-[#9CA3AF] text-white" :
                            "bg-[#CD7F32] text-white"
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-medium truncate">{mart.name}</div>
                            <div className="text-[#6EE7B7] text-xs">{mart.county}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-white font-mono text-sm">€{mart.avg_price?.toFixed(2)}</div>
                            <div className={`text-xs ${getChangeColor(mart.change)}`}>
                              {mart.change > 0 ? "+" : ""}{mart.change?.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analysis */}
                {weeklyReport.analysis && (
                  <div className="p-6 border-t border-[#065F46]">
                    <h3 className="text-sm font-mono uppercase text-[#6EE7B7] mb-3">Market Analysis</h3>
                    <div className="text-[#A7F3D0] text-sm leading-relaxed space-y-3">
                      {weeklyReport.analysis.split('\n').map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ) : (
              <div className="bg-[#064E3B] border border-[#065F46] p-8 text-center">
                <Newspaper size={48} className="mx-auto text-[#6EE7B7] mb-4" />
                <p className="text-[#A7F3D0]">Weekly report generating...</p>
              </div>
            )}

            {/* Mart News */}
            <div className="bg-[#064E3B] border border-[#065F46]">
              <div className="px-6 py-4 border-b border-[#065F46] flex items-center justify-between">
                <h2 className="font-serif text-xl text-white flex items-center gap-2">
                  <Newspaper size={20} />
                  Mart News
                </h2>
                <Link to="/news?category=markets" className="text-sm text-[#6EE7B7] hover:underline flex items-center gap-1">
                  All News <CaretRight size={14} />
                </Link>
              </div>
              <div className="divide-y divide-[#065F46]">
                {martNews.length > 0 ? martNews.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="block p-4 hover:bg-[#065F46]/50 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#6EE7B7]">{article.source}</span>
                      <span className="text-xs text-[#6EE7B7]/50">•</span>
                      <span className="text-xs text-[#6EE7B7]/70 flex items-center gap-1">
                        <Clock size={10} />
                        {formatShortDate(article.published_at)}
                      </span>
                    </div>
                    <h3 className="text-white hover:text-[#6EE7B7] transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                  </Link>
                )) : (
                  <div className="p-6 text-center text-[#6EE7B7]/70">
                    No mart news available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Top of the Marts Widget */}
            <TopOfTheMarts />

            {/* Quick Stats */}
            {weeklyReport?.quick_stats && (
              <div className="bg-[#064E3B] border border-[#065F46] p-4">
                <h3 className="text-sm font-mono uppercase text-[#6EE7B7] mb-4">This Week's Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#A7F3D0] text-sm">Total Sales</span>
                    <span className="text-white font-mono">{weeklyReport.quick_stats.total_sales?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A7F3D0] text-sm">Avg. Price</span>
                    <span className="text-white font-mono">€{weeklyReport.quick_stats.avg_price?.toFixed(2)}/kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A7F3D0] text-sm">Active Marts</span>
                    <span className="text-white font-mono">{weeklyReport.quick_stats.active_marts}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A7F3D0] text-sm">Week Trend</span>
                    <span className={`font-mono flex items-center gap-1 ${getChangeColor(weeklyReport.quick_stats.week_trend)}`}>
                      {getChangeIcon(weeklyReport.quick_stats.week_trend)}
                      {weeklyReport.quick_stats.week_trend > 0 ? "+" : ""}{weeklyReport.quick_stats.week_trend?.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Market Calendar - placeholder */}
            <div className="bg-[#064E3B] border border-[#065F46] p-4">
              <h3 className="text-sm font-mono uppercase text-[#6EE7B7] mb-3 flex items-center gap-2">
                <CalendarBlank size={16} />
                Upcoming Sales
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-[#A7F3D0]">
                  <span>Monday</span>
                  <span className="text-white">Ennis, Kilkenny, Cork</span>
                </div>
                <div className="flex justify-between text-[#A7F3D0]">
                  <span>Tuesday</span>
                  <span className="text-white">Tullamore, Carrick</span>
                </div>
                <div className="flex justify-between text-[#A7F3D0]">
                  <span>Wednesday</span>
                  <span className="text-white">Athenry, Roscommon</span>
                </div>
                <div className="flex justify-between text-[#A7F3D0]">
                  <span>Thursday</span>
                  <span className="text-white">Cahir, Bandon</span>
                </div>
                <div className="flex justify-between text-[#A7F3D0]">
                  <span>Friday</span>
                  <span className="text-white">Baltinglass, Skibbereen</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
